import { apiPost, planningPath } from './PlanningApiClient';

/** Another guest of the project that shares a phone or email. The API sends only these four fields. */
export interface DuplicateGuestRef {
  id: string;
  reference_id: number | null;
  label: string;
  name: string;
}

/** A non-blocking warning from the API: the guest is saved (or can be saved) anyway. */
export interface GuestContactWarning {
  code: 'DUPLICATE_PHONE' | 'DUPLICATE_EMAIL';
  field: 'phone' | 'email';
  message: string;
  compared_as: 'e164' | 'as_given' | 'email';
  /** The other guest; null when the match is another row of the same batch. */
  guest: DuplicateGuestRef | null;
  /** 0-based index of the other row of the same batch; null when the match is a saved guest. */
  row: number | null;
}

export interface GuestContactCheckItem {
  phone?: string | null;
  email?: string | null;
  /** Guest ref (GS-###) being edited, so it is never reported against itself. */
  exclude_guest?: string | null;
}

/**
 * Asks the API whether another guest already uses this phone or email. The server does the
 * comparing (normalising the number, matching the email); this only carries the answer.
 * A failed check yields no warnings rather than an error: a warning is a courtesy, never a gate.
 */
export async function checkGuestContact(item: GuestContactCheckItem): Promise<GuestContactWarning[]> {
  try {
    const res = await apiPost<{ warnings?: GuestContactWarning[] }>(planningPath('/guests/contact-check'), item);
    return Array.isArray(res?.warnings) ? res.warnings : [];
  } catch {
    return [];
  }
}

/** Many rows at once (import preview). One warning list per row, in order; [] lists when the check fails. */
export async function checkGuestContacts(items: GuestContactCheckItem[]): Promise<GuestContactWarning[][]> {
  if (items.length === 0) return [];
  try {
    const res = await apiPost<{ results?: Array<{ warnings?: GuestContactWarning[] }> }>(planningPath('/guests/contact-check'), { items });
    const results = Array.isArray(res?.results) ? res.results : [];
    return items.map((_, index) => (Array.isArray(results[index]?.warnings) ? (results[index].warnings as GuestContactWarning[]) : []));
  } catch {
    return items.map(() => []);
  }
}

/** The warnings the write itself returned next to the saved guest (create and update responses). */
export function warningsFromSaveResponse(response: unknown): GuestContactWarning[] {
  const warnings = (response as { warnings?: unknown } | null | undefined)?.warnings;
  return Array.isArray(warnings) ? (warnings as GuestContactWarning[]) : [];
}

/** Window event carrying the non-blocking warnings a guest save returned (a phone or email another guest already uses). */
export const GUEST_SAVE_WARNINGS_EVENT = 'guest-save-warnings';
